<template>
  <div class="lg-page">
    <div class="lg-bg" :class="{ 'is-blurred': isAuthenticating }" aria-hidden="true"></div>

    <!-- ===================================================== -->
    <!-- LOADER                                                -->
    <!-- ===================================================== -->
    <transition name="lg-overlay">
      <div v-if="isAuthenticating" class="lg-loader" role="status" aria-live="polite">
        <div class="lg-loader-card" :class="{ 'is-done': loadSuccess }">

          <div class="lg-ring">
            <svg viewBox="0 0 120 120" class="lg-ring-svg" aria-hidden="true">
              <circle class="lg-ring-track" cx="60" cy="60" r="52" />
              <circle
                class="lg-ring-progress"
                cx="60" cy="60" r="52"
                :stroke-dasharray="ringCircumference"
                :stroke-dashoffset="ringOffset"
              />
              <circle v-if="!loadSuccess" class="lg-ring-orbit" cx="60" cy="60" r="43" />
            </svg>
            <transition name="lg-swap" mode="out-in">
              <span v-if="!loadSuccess" key="mark" class="lg-ring-mark">FM</span>
              <span v-else key="check" class="lg-ring-check">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
              </span>
            </transition>
          </div>

          <div class="lg-loader-head">
            <div class="lg-loader-title">{{ loadSuccess ? 'You’re signed in' : loadingMessage }}</div>
            <div class="lg-loader-pct">{{ Math.round(loadingProgress) }}%</div>
          </div>

          <ol class="lg-steps">
            <li v-for="(step, i) in loaderSteps" :key="step" class="lg-step" :class="'is-' + stepState(i)">
              <span class="lg-step-dot">
                <svg v-if="stepState(i) === 'done'" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 12.5l4 4L18 8.5" /></svg>
              </span>
              <span class="lg-step-label">{{ step }}</span>
            </li>
          </ol>

          <transition name="lg-tip" mode="out-in">
            <p :key="currentTipIndex" class="lg-loader-tip">{{ currentTip }}</p>
          </transition>
        </div>
      </div>
    </transition>

    <!-- ===================================================== -->
    <!-- AUTH CARD                                             -->
    <!-- ===================================================== -->
    <div class="lg-shell" :class="{ 'is-loaded': isContentLoaded, 'is-blurred': isAuthenticating }">
      <div class="lg-card">

        <!-- LEFT: FORM -->
        <section class="lg-form-pane" :class="{ 'is-hidden': showResetPassword }">
          <div class="lg-brand-mark">
            <div class="lg-brand-logo">FM</div>
            <div>
              <div class="lg-brand-name">Furniture Mecca</div>
              <div class="lg-brand-sub">Inventory system</div>
            </div>
          </div>

          <transition name="lg-step-swap" mode="out-in">
            <!-- Step 1: credentials -->
            <div v-if="!showLocationSelector" key="creds" class="lg-form-block">
              <header class="lg-form-title">
                <h1>Welcome back</h1>
                <p>Sign in with your work email to open your dashboard.</p>
              </header>

              <form @submit="onSubmit" autocomplete="off" class="lg-form" :class="{ 'is-shaking': isShaking }" novalidate>
                <div class="lg-field">
                  <label for="lg-email" class="lg-field-label">Email</label>
                  <div class="lg-control" :class="{ 'is-error': !!formError, 'is-disabled': input_submit_disabled }">
                    <span class="lg-ico lg-control-icon"><b-icon icon="envelope"></b-icon></span>
                    <input
                      id="lg-email"
                      v-model="form.username"
                      type="text"
                      class="lg-control-input"
                      placeholder="you@furnituremecca.com"
                      :disabled="input_submit_disabled"
                      @focus="onFieldFocus"
                      @blur="onFieldBlur"
                    />
                  </div>
                </div>

                <div class="lg-field">
                  <label for="lg-password" class="lg-field-label">Password</label>
                  <div class="lg-control" :class="{ 'is-error': !!formError, 'is-disabled': input_submit_disabled }">
                    <span class="lg-ico lg-control-icon"><b-icon icon="lock"></b-icon></span>
                    <input
                      id="lg-password"
                      v-model="form.password"
                      :type="showPassword ? 'text' : 'password'"
                      class="lg-control-input"
                      placeholder="Enter your password"
                      :disabled="input_submit_disabled"
                      @focus="onFieldFocus"
                      @blur="onFieldBlur"
                      @keyup="onPasswordKey"
                      @keydown="onPasswordKey"
                    />
                    <button
                      type="button"
                      class="lg-eye-btn"
                      :aria-label="showPassword ? 'Hide password' : 'Show password'"
                      :disabled="input_submit_disabled"
                      @click="togglePasswordVisibility"
                    >
                      <span class="lg-ico"><b-icon :icon="showPassword ? 'eye-slash' : 'eye'"></b-icon></span>
                    </button>
                  </div>
                  <transition name="lg-fade">
                    <span v-if="capsLockOn" class="lg-hint">
                      <span class="lg-ico"><b-icon icon="exclamation-triangle-fill"></b-icon></span>
                      Caps Lock is on
                    </span>
                  </transition>
                </div>

                <div class="lg-form-options">
                  <label class="lg-check" :class="{ 'is-disabled': input_submit_disabled }">
                    <input type="checkbox" v-model="form.rememberMe" :disabled="input_submit_disabled" />
                    <span class="lg-check-box" aria-hidden="true">
                      <svg viewBox="0 0 24 24"><path d="M6 12.5l4 4L18 8.5" /></svg>
                    </span>
                    <span>Remember me</span>
                  </label>
                  <a href="#" class="lg-link" @click.prevent="showResetPasswordForm">Forgot password?</a>
                </div>

                <transition name="lg-fade-up">
                  <div v-if="formError" class="lg-alert" role="alert">
                    <span class="lg-ico lg-alert-icon"><b-icon icon="exclamation-circle-fill"></b-icon></span>
                    <div>
                      <div class="lg-alert-msg">{{ formError }}</div>
                      <div v-if="failedAttempts > 0 && failedAttempts < 3" class="lg-attempts">
                        <span class="lg-attempt-pips" aria-hidden="true">
                          <i v-for="n in 3" :key="n" :class="{ 'is-used': n <= failedAttempts }"></i>
                        </span>
                        <span>
                          {{ failedAttempts === 2 ? 'Last attempt before password reset' : (3 - failedAttempts) + ' attempts left' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </transition>

                <button
                  type="submit"
                  class="lg-btn-primary"
                  :class="{ 'is-loading': input_submit_disabled }"
                  :disabled="input_submit_disabled"
                >
                  <template v-if="!input_submit_disabled">
                    <span>Sign in</span>
                    <span class="lg-ico lg-btn-arrow"><b-icon icon="arrow-right-short"></b-icon></span>
                  </template>
                  <span v-else class="lg-btn-loader"><span class="lg-dot"></span><span class="lg-dot"></span><span class="lg-dot"></span></span>
                </button>
              </form>
            </div>

            <!-- Step 2: location -->
            <div v-else key="loc" class="lg-form-block">
              <header class="lg-form-title">
                <h1>Choose your store</h1>
                <p>Signed in as <strong>{{ form.username }}</strong>. Pick where you’re working today.</p>
              </header>

              <form @submit="onSubmit" class="lg-form" :class="{ 'is-shaking': isShaking }" novalidate>
                <div v-if="locationOptions.length <= 6" class="lg-loc-list" role="radiogroup" aria-label="Store location">
                  <button
                    v-for="opt in locationOptions"
                    :key="opt.value"
                    type="button"
                    role="radio"
                    class="lg-loc"
                    :class="{ 'is-selected': selectedLocation === opt.value }"
                    :aria-checked="selectedLocation === opt.value ? 'true' : 'false'"
                    :disabled="input_submit_disabled"
                    @click="selectedLocation = opt.value"
                  >
                    <span class="lg-loc-icon"><span class="lg-ico"><b-icon icon="geo-alt"></b-icon></span></span>
                    <span class="lg-loc-name">{{ opt.text }}</span>
                    <span class="lg-loc-radio" aria-hidden="true"></span>
                  </button>
                </div>

                <div v-else class="lg-field">
                  <label for="lg-store" class="lg-field-label">Store</label>
                  <div class="lg-control lg-control--select" :class="{ 'is-disabled': input_submit_disabled }">
                    <span class="lg-ico lg-control-icon"><b-icon icon="geo-alt"></b-icon></span>
                    <select id="lg-store" v-model="selectedLocation" class="lg-control-input" :disabled="input_submit_disabled">
                      <option :value="null" disabled>Select a store</option>
                      <option v-for="opt in locationOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                    </select>
                    <span class="lg-ico lg-select-caret"><b-icon icon="chevron-down"></b-icon></span>
                  </div>
                </div>

                <transition name="lg-fade-up">
                  <div v-if="formError" class="lg-alert" role="alert">
                    <span class="lg-ico lg-alert-icon"><b-icon icon="exclamation-circle-fill"></b-icon></span>
                    <div class="lg-alert-msg">{{ formError }}</div>
                  </div>
                </transition>

                <button
                  type="submit"
                  class="lg-btn-primary"
                  :class="{ 'is-loading': input_submit_disabled }"
                  :disabled="input_submit_disabled || !selectedLocation"
                >
                  <template v-if="!input_submit_disabled">
                    <span>Continue to {{ selectedLocationName || 'store' }}</span>
                    <span class="lg-ico lg-btn-arrow"><b-icon icon="arrow-right-short"></b-icon></span>
                  </template>
                  <span v-else class="lg-btn-loader"><span class="lg-dot"></span><span class="lg-dot"></span><span class="lg-dot"></span></span>
                </button>

                <button type="button" class="lg-btn-text" @click="backToCredentials" :disabled="input_submit_disabled">
                  <span class="lg-ico"><b-icon icon="arrow-left"></b-icon></span>
                  <span>Use a different account</span>
                </button>
              </form>
            </div>
          </transition>
        </section>

        <!-- RIGHT: BRAND / RESET -->
        <aside class="lg-side-pane">
          <transition name="lg-pane-slide" mode="out-in">

            <!-- Brand view -->
            <div v-if="!showResetPassword" key="brand" class="lg-side-content">
              <svg class="lg-route" viewBox="0 0 320 96" aria-hidden="true">
                <path class="lg-route-base" d="M24 70 C 90 70, 100 26, 160 26 S 236 70, 296 54" />
                <path class="lg-route-live" d="M24 70 C 90 70, 100 26, 160 26 S 236 70, 296 54" />
                <g class="lg-route-node"><circle cx="24" cy="70" r="6" /><text x="24" y="92">Warehouse</text></g>
                <g class="lg-route-node"><circle cx="160" cy="26" r="6" /><text x="160" y="12">Store</text></g>
                <g class="lg-route-node is-end"><circle cx="296" cy="54" r="7" /><text x="296" y="80">Customer</text></g>
              </svg>

              <div class="lg-side-brand">
                <h2 class="lg-side-title">Furniture Mecca</h2>
                <div class="lg-side-sub">Rugs &amp; Mattresses</div>
                <p class="lg-side-tagline">Inventory, deliveries and customer care for every store, in one place.</p>
              </div>

              <ul class="lg-side-list">
                <li>
                  <div class="lg-side-icon"><span class="lg-ico"><b-icon icon="box-seam"></b-icon></span></div>
                  <div><strong>Live inventory</strong><span>Stock across stores and warehouses as it changes.</span></div>
                </li>
                <li>
                  <div class="lg-side-icon"><span class="lg-ico"><b-icon icon="truck"></b-icon></span></div>
                  <div><strong>Envio by Vista</strong><span>Dispatch, tracking and delivery status together.</span></div>
                </li>
                <li>
                  <div class="lg-side-icon"><span class="lg-ico"><b-icon icon="graph-up"></b-icon></span></div>
                  <div><strong>Business insights</strong><span>Sales, performance and reporting dashboards.</span></div>
                </li>
              </ul>

              <div class="lg-side-foot">© {{ currentYear }} Furniture Mecca. All rights reserved.</div>
            </div>

            <!-- Reset view -->
            <div v-else key="reset" class="lg-side-content">
              <button type="button" class="lg-back-btn" @click="hideResetPasswordForm">
                <span class="lg-ico"><b-icon icon="arrow-left"></b-icon></span>
                <span>Back to sign in</span>
              </button>

              <transition name="lg-step-swap" mode="out-in">
                <div v-if="!resetSent" key="form" class="lg-reset-block">
                  <div class="lg-side-brand">
                    <h2 class="lg-side-title is-sm">Reset your password</h2>
                    <p class="lg-side-tagline">Enter the email on your account and we’ll send you a link to set a new password.</p>
                  </div>

                  <form @submit="sendPasswordResetRequest" class="lg-reset-form" novalidate>
                    <div class="lg-field">
                      <label for="lg-reset-email" class="lg-field-label">Email address</label>
                      <div class="lg-control is-dark" :class="{ 'is-disabled': resetPasswordSending }">
                        <span class="lg-ico lg-control-icon"><b-icon icon="envelope"></b-icon></span>
                        <input
                          id="lg-reset-email"
                          v-model="resetEmail"
                          type="email"
                          class="lg-control-input"
                          placeholder="you@furnituremecca.com"
                          :disabled="resetPasswordSending"
                          autocomplete="off"
                        />
                      </div>
                    </div>

                    <transition name="lg-fade-up">
                      <div v-if="resetError" class="lg-alert is-dark" role="alert">
                        <span class="lg-ico lg-alert-icon"><b-icon icon="exclamation-circle-fill"></b-icon></span>
                        <div class="lg-alert-msg">{{ resetError }}</div>
                      </div>
                    </transition>

                    <button type="submit" class="lg-btn-primary" :disabled="resetPasswordSending || !resetEmail">
                      <template v-if="!resetPasswordSending">
                        <span class="lg-ico"><b-icon icon="envelope"></b-icon></span>
                        <span>Send reset link</span>
                      </template>
                      <span v-else class="lg-btn-loader"><span class="lg-dot"></span><span class="lg-dot"></span><span class="lg-dot"></span></span>
                    </button>
                  </form>
                </div>

                <div v-else key="sent" class="lg-reset-block lg-sent">
                  <div class="lg-sent-icon">
                    <svg viewBox="0 0 48 48" aria-hidden="true">
                      <rect x="6" y="11" width="36" height="26" rx="5" />
                      <path d="M8 14l16 12 16-12" />
                    </svg>
                  </div>
                  <h2 class="lg-side-title is-sm">Check your inbox</h2>
                  <p class="lg-side-tagline">
                    We sent a reset link to <strong>{{ resetEmail }}</strong>. The link expires in 30 minutes.
                  </p>
                  <div class="lg-sent-actions">
                    <button type="button" class="lg-btn-primary" @click="hideResetPasswordForm">Back to sign in</button>
                    <button type="button" class="lg-btn-text is-light" @click="resetSent = false">Use a different email</button>
                  </div>
                </div>
              </transition>

              <div class="lg-side-foot">Need help? Contact your store administrator.</div>
            </div>
          </transition>
        </aside>
      </div>
    </div>
  </div>
</template>

<script>
import Requestor from '@/utilities/Requestor'

const RING_RADIUS = 52;

export default {
  name: "Login",
  data() {
    return {
      form: { username: null, password: null, rememberMe: false },

      // Location step
      selectedLocation: null,
      locationOptions: [],
      showLocationSelector: false,
      tempUserDetails: null,

      // UI state
      input_submit_disabled: false,
      isContentLoaded: false,
      focusedField: null,
      showPassword: false,
      capsLockOn: false,
      formError: '',
      isShaking: false,

      // Loader
      isAuthenticating: false,
      loadSuccess: false,
      loadingMessage: 'Signing you in',
      loadingProgress: 0,
      currentTipIndex: 0,
      loadingInterval: null,
      tipInterval: null,
      loaderSteps: [
        'Verifying credentials',
        'Loading stores and inventory',
        'Preparing your dashboard'
      ],
      tips: [
        'Syncing stock across stores and warehouses',
        'Fetching today’s deliveries',
        'Loading vendors and categories',
        'Almost ready'
      ],

      // Reset password
      showResetPassword: false,
      resetEmail: '',
      resetPasswordSending: false,
      resetSent: false,
      resetSentMessage: '',
      resetError: '',

      // Failed attempts (3 strikes → reset password)
      failedAttempts: 0,
      lastFailedUsername: null
    }
  },

  computed: {
    currentTip() {
      return this.tips[this.currentTipIndex];
    },
    currentYear() {
      return new Date().getFullYear();
    },
    ringCircumference() {
      return 2 * Math.PI * RING_RADIUS;
    },
    ringOffset() {
      return this.ringCircumference * (1 - Math.min(this.loadingProgress, 100) / 100);
    },
    selectedLocationName() {
      const opt = this.locationOptions.find(o => o.value === this.selectedLocation);
      return opt ? opt.text : '';
    }
  },

  watch: {
    'form.username'() { if (this.formError && !this.isShaking) this.formError = ''; },
    'form.password'() { if (this.formError && !this.isShaking) this.formError = ''; },
    resetEmail() { this.resetError = ''; }
  },

  created() {
    const userToken = localStorage.getItem("userToken");
    if (userToken) this.$router.push({ path: "/home/" });
  },

  mounted() {
    setTimeout(() => { this.isContentLoaded = true; }, 80);
  },

  beforeDestroy() {
    this.clearLoaderTimers();
  },

  methods: {
    // ---------- Field helpers ----------
    onFieldFocus() {
      if (this.form.username && this.lastFailedUsername && this.form.username !== this.lastFailedUsername) {
        this.failedAttempts = 0;
        this.lastFailedUsername = null;
      }
    },
    onFieldBlur() {
      this.focusedField = null;
    },
    onPasswordKey(e) {
      if (e && typeof e.getModifierState === 'function') {
        this.capsLockOn = e.getModifierState('CapsLock');
      }
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    shake() {
      this.isShaking = true;
      setTimeout(() => { this.isShaking = false; }, 450);
    },

    // ---------- Failed attempts ----------
    handleFailedAttempt() {
      this.failedAttempts++;
      this.lastFailedUsername = this.form.username;
      this.shake();

      if (this.failedAttempts < 3) {
        this.formError = 'That email and password don’t match. Check them and try again.';
        return;
      }

      this.formError = 'Too many failed attempts. Reset your password to continue.';
      setTimeout(() => {
        this.resetEmail = this.form.username || '';
        this.resetSent = false;
        this.showResetPassword = true;
        this.failedAttempts = 0;
        this.lastFailedUsername = null;
        this.formError = '';
      }, 1500);
    },

    // ---------- Reset password ----------
    showResetPasswordForm() {
      if (this.form.username) this.resetEmail = this.form.username;
      this.resetSent = false;
      this.resetError = '';
      this.showResetPassword = true;
    },
    hideResetPasswordForm() {
      this.showResetPassword = false;
      this.resetSent = false;
      this.resetSentMessage = '';
      this.failedAttempts = 0;
      this.lastFailedUsername = null;
      this.formError = '';
    },

    // ---------- Loader ----------
    stepState(i) {
      if (this.loadSuccess) return 'done';
      const thresholds = [0, 34, 68, 101];
      if (this.loadingProgress >= thresholds[i + 1]) return 'done';
      if (this.loadingProgress >= thresholds[i]) return 'active';
      return 'pending';
    },
    startLoadingAnimation(message = 'Signing you in') {
      this.clearLoaderTimers();
      this.isAuthenticating = true;
      this.loadSuccess = false;
      this.loadingMessage = message;
      this.loadingProgress = 0;
      this.currentTipIndex = 0;

      this.loadingInterval = setInterval(() => {
        if (this.loadingProgress < 90) {
          // Fast at first, slowing as it approaches 90%
          const step = Math.max(1.2, (90 - this.loadingProgress) * 0.08) + Math.random() * 3;
          this.loadingProgress = Math.min(90, this.loadingProgress + step);
        }
      }, 260);

      this.tipInterval = setInterval(() => {
        if (this.currentTipIndex < this.tips.length - 1) this.currentTipIndex++;
        else clearInterval(this.tipInterval);
      }, 1800);
    },
    // Finish the ring and show the check mark; overlay stays up until navigation
    completeLoadingAnimation() {
      this.clearLoaderTimers();
      this.loadingProgress = 100;
      this.loadSuccess = true;
    },
    stopLoadingAnimation(success = true) {
      if (success) {
        this.completeLoadingAnimation();
        setTimeout(() => {
          this.isAuthenticating = false;
          this.loadSuccess = false;
        }, 650);
      } else {
        this.clearLoaderTimers();
        this.isAuthenticating = false;
        this.loadSuccess = false;
      }
    },
    clearLoaderTimers() {
      if (this.loadingInterval) clearInterval(this.loadingInterval);
      if (this.tipInterval) clearInterval(this.tipInterval);
      this.loadingInterval = null;
      this.tipInterval = null;
    },

    // ---------- Flow ----------
    backToCredentials() {
      this.showLocationSelector = false;
      this.selectedLocation = null;
      this.formError = '';
    },
    onSubmit(e) {
      e.preventDefault();
      this.formError = '';
      if (!this.showLocationSelector) {
        if (!this.form.username || !this.form.password) {
          this.formError = 'Enter your email and password to sign in.';
          this.shake();
          return;
        }
        this.authenticateUser();
      } else {
        this.completeLoginWithLocation();
      }
    },

    authenticateUser() {
      const that = this;
      const path = "/api/user/login/";
      const data = { username: this.form.username, password: this.form.password };

      this.startLoadingAnimation('Signing you in');

      const s = function (response) {
        that.input_submit_disabled = false;
        if (!response.data) return;

        if (response.data.error) {
          that.stopLoadingAnimation(false);
          that.handleFailedAttempt();
          return;
        }

        that.failedAttempts = 0;
        that.lastFailedUsername = null;

        if (response.data.needs_location_selection) {
          that.stopLoadingAnimation(true);
          setTimeout(() => {
            that.tempUserDetails = response.data.user_details;
            that.locationOptions = response.data.location_options.map(loc => ({ value: loc.value, text: loc.text }));
            that.showLocationSelector = true;
          }, 500);
          return;
        }

        const userToken = response.data.token;
        if (userToken) {
          localStorage.setItem("userToken", userToken);
          localStorage.setItem("userDetails", JSON.stringify(response.data.user_details));
          if (response.data.selected_location) localStorage.setItem("selectedLocation", response.data.selected_location);
          that.$root.$emit("authenticated", true);
          that.completeLoadingAnimation();
          setTimeout(() => { that.fetch_prereqs(); }, 600);
        }
      };

      const f = function () {
        that.stopLoadingAnimation(false);
        that.handleFailedAttempt();
        that.input_submit_disabled = false;
      };

      that.input_submit_disabled = true;
      Requestor.postSync(path, data, s, f);
    },

    completeLoginWithLocation() {
      if (!this.selectedLocation) {
        this.formError = 'Select a store to continue.';
        this.shake();
        return;
      }

      const that = this;
      const path = "/api/user/complete_login/";
      const data = {
        user_id: this.tempUserDetails.id,
        selected_location: this.selectedLocation,
        username: this.form.username
      };

      this.startLoadingAnimation(`Opening ${this.selectedLocationName || 'your store'}`);

      const s = function (response) {
        that.input_submit_disabled = false;
        if (!response.data) return;

        if (response.data.error) {
          that.stopLoadingAnimation(false);
          that.formError = response.data.error;
          that.shake();
          return;
        }

        const userToken = response.data.token;
        if (userToken) {
          localStorage.setItem("userToken", userToken);
          const userDetails = response.data.user_details;
          userDetails.location_id = that.selectedLocation;
          if (userDetails.properties) userDetails.properties.location_id = that.selectedLocation;
          localStorage.setItem("userDetails", JSON.stringify(userDetails));
          localStorage.setItem("selectedLocation", that.selectedLocation);
          localStorage.setItem("userLocations", JSON.stringify(response.data.user_locations || []));
          that.$root.$emit("authenticated", true);
          that.completeLoadingAnimation();
          setTimeout(() => { that.fetch_prereqs(); }, 600);
        }
      };

      const f = function (error) {
        that.stopLoadingAnimation(false);
        that.formError = (error && error.response && error.response.data && error.response.data.error)
          || 'Couldn’t open that store. Try again.';
        that.shake();
        that.input_submit_disabled = false;
      };

      that.input_submit_disabled = true;
      Requestor.postSync(path, data, s, f);
    },

    fetch_prereqs() {
      const path = "/api/user/get_tools_packet/";
      const that = this;

      const s = function (response) {
        if (response.data.result) {
          const r = response.data.result;
          const locations = (r.locations || []).map(d => ({
            value: d.id, text: d.name, short_name: d.short_name, address: d.address,
            email: d.email, phone: d.phone, allowed: d.allowed,
            sales_tax: parseFloat(d.sales_tax), is_main_warehouse: d.is_main_warehouse,
          }));
          locations.splice(0, 0, { value: null, text: "All", short: "None", address: null, email: null, phone: null, allowed: [] });
          const toDict = arr => arr.reduce((map, obj) => (map[obj.value] = obj.text, map), {});

          const sublocations = (r.sublocations || []).map(d => ({ value: d.id, text: d.name, location_id: d.location_id }));
          const locsubs = r.locsubs || [];
          const vendors = r.vendors || [];
          const categories = r.categories || [];
          const trucks = (r.trucks || []).map(d => ({ value: d.value, text: d.text }));
          const housetrucks = (r.housetrucks || []).map(d => ({ value: d.value, text: d.text }));
          const deliverytrucks = (r.deliverytrucks || []).map(d => ({ value: d.value, text: d.text }));

          const packet = {
            locations, locationsdict: toDict(locations),
            sublocations, sublocationsdict: toDict(sublocations),
            locsubs, locsubsdict: toDict(locsubs),
            vendors, vendorsdict: toDict(vendors),
            categories, categoriesdict: toDict(categories),
            trucks, trucksdict: toDict(trucks),
            housetrucks, deliverytrucks
          };
          localStorage.setItem("packet", JSON.stringify(packet));
          that.$router.push({ path: "/home/" });
        } else {
          that.stopLoadingAnimation(false);
          that.formError = response.data.error || 'Signed in, but your data didn’t load. Refresh to try again.';
        }
      };

      const f = function () {
        that.stopLoadingAnimation(false);
        that.formError = 'Signed in, but your data didn’t load. Refresh to try again.';
      };

      Requestor.getSyncP(path, {}, this, s, f);
    },

    sendPasswordResetRequest(e) {
      e.preventDefault();
      const that = this;
      const path = "/api/user/request-password-reset/";
      const data = { username: this.resetEmail, frontend_url: window.location.origin };

      this.resetPasswordSending = true;
      this.resetError = '';

      const s = function (response) {
        that.resetPasswordSending = false;
        if (response.data && response.data.message) {
          that.resetSentMessage = '';
          that.resetSent = true;
        } else if (response.data && response.data.error) {
          that.resetError = response.data.error;
        }
      };

      const f = function (error) {
        that.resetPasswordSending = false;
        that.resetError = (error && error.response && error.response.data && error.response.data.error)
          || 'The reset link wasn’t sent. Check the email address and try again.';
      };

      Requestor.postSync(path, data, s, f);
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Sora:wght@600;700;800&display=swap');
</style>

<style scoped>
/* ===================================================== */
/* TOKENS + RESET (guards against global Bootstrap CSS)  */
/* ===================================================== */
.lg-page {
  --fm-orange: #EE7A22;
  --fm-orange-dark: #D4620F;
  --fm-orange-soft: #FFF1E4;
  --fm-amber: #FFB06B;
  --fm-ink: #221F1C;
  --fm-ink-2: #4A4540;
  --fm-muted: #736C63;
  --fm-faint: #A59D92;
  --fm-line: #E5E1DA;
  --fm-paper: #F7F5F2;
  --fm-teal: #0F6E6B;
  --fm-danger: #B42318;
  --fm-danger-soft: #FEF3F2;
  --fm-ease: cubic-bezier(0.22, 1, 0.36, 1);

  position: relative;
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  font-family: 'Manrope', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  color: var(--fm-ink);
  overflow-x: hidden;
  box-sizing: border-box;
}
.lg-page *, .lg-page *::before, .lg-page *::after { box-sizing: border-box; }
.lg-page button { font-family: inherit; margin: 0; }
.lg-page label { margin: 0; }

/* Icon wrapper: the ONLY thing we style; b-icon itself stays untouched */
.lg-ico {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1em;
  height: 1em;
  line-height: 1;
  position: relative;
}
.lg-ico >>> svg {
  display: block;
  width: 1em !important;
  height: 1em !important;
  position: static !important;
  margin: 0 !important;
  vertical-align: middle !important;
  transform: none;
}


/* Hard centering for icon/logo boxes: overrides any global span/div display rules */
.lg-ico, .lg-brand-logo, .lg-side-icon, .lg-loc-icon, .lg-eye-btn,
.lg-ring-mark, .lg-ring-check, .lg-step-dot, .lg-check-box, .lg-sent-icon {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  text-align: center !important;
  line-height: 1 !important;
  padding: 0 !important;
}
.lg-ico { display: inline-flex !important; }
.lg-brand-logo { width: 42px !important; height: 42px !important; }
.lg-side-icon { width: 40px !important; height: 40px !important; }
.lg-loc-icon { width: 36px !important; height: 36px !important; }

/* ===================================================== */
/* BACKGROUND                                            */
/* ===================================================== */
.lg-bg { position: fixed; inset: 0; z-index: -2; background: #F6F4F0; }
.lg-bg::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
  background-size: cover;
  background-position: center;
  filter: blur(16px) saturate(0.9);
  opacity: 0.42;
  transform: scale(1.1);
  transition: filter 0.4s ease;
}
.lg-bg.is-blurred::after { filter: blur(28px) saturate(0.8); }

/* ===================================================== */
/* LOADER                                                */
/* ===================================================== */
.lg-loader {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(34, 31, 28, 0.78);
  backdrop-filter: blur(6px);
}
.lg-loader-card {
  width: 100%;
  max-width: 360px;
  background: #fff;
  border-radius: 24px;
  padding: 32px 28px 24px;
  text-align: center;
  box-shadow: 0 40px 80px -30px rgba(0, 0, 0, 0.6);
  animation: lgCardIn 0.45s var(--fm-ease);
}
@keyframes lgCardIn {
  from { opacity: 0; transform: translateY(14px) scale(0.97); }
  to   { opacity: 1; transform: none; }
}

.lg-ring { position: relative; width: 112px; height: 112px; margin: 0 auto 20px; }
.lg-ring-svg { display: block; width: 100%; height: 100%; transform: rotate(-90deg); }
.lg-ring-track { fill: none; stroke: #F1EEE9; stroke-width: 6; }
.lg-ring-progress {
  fill: none;
  stroke: var(--fm-orange);
  stroke-width: 6;
  stroke-linecap: round;
  transition: stroke-dashoffset 0.4s var(--fm-ease), stroke 0.3s ease;
}
.lg-ring-orbit {
  fill: none;
  stroke: var(--fm-amber);
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-dasharray: 26 244;
  transform-origin: 60px 60px;
  animation: lgOrbit 1.1s linear infinite;
}
@keyframes lgOrbit { to { transform: rotate(360deg); } }

.lg-ring-mark, .lg-ring-check {
  position: absolute;
  top: 22px; left: 22px; right: 22px; bottom: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.lg-ring-mark {
  background: var(--fm-orange);
  color: #fff;
  font-family: 'Sora', sans-serif;
  font-weight: 800;
  font-size: 22px;
  letter-spacing: -0.02em;
  animation: lgBreathe 1.6s ease-in-out infinite;
}
@keyframes lgBreathe { 0%, 100% { transform: scale(1); } 50% { transform: scale(0.93); } }
.lg-ring-check { background: var(--fm-teal); }
.lg-ring-check svg {
  width: 34px; height: 34px;
  fill: none; stroke: #fff; stroke-width: 2.6;
  stroke-linecap: round; stroke-linejoin: round;
  stroke-dasharray: 24; stroke-dashoffset: 24;
  animation: lgDraw 0.4s 0.1s var(--fm-ease) forwards;
}
@keyframes lgDraw { to { stroke-dashoffset: 0; } }
.lg-loader-card.is-done .lg-ring-progress { stroke: var(--fm-teal); }

.lg-swap-enter-active, .lg-swap-leave-active { transition: opacity 0.2s ease, transform 0.25s var(--fm-ease); }
.lg-swap-enter, .lg-swap-leave-to { opacity: 0; transform: scale(0.6); }

.lg-loader-head { display: flex; align-items: baseline; justify-content: center; gap: 10px; margin-bottom: 20px; }
.lg-loader-title { font-family: 'Sora', sans-serif; font-size: 19px; font-weight: 700; letter-spacing: -0.01em; }
.lg-loader-pct { font-size: 13px; font-weight: 700; color: var(--fm-muted); font-variant-numeric: tabular-nums; min-width: 3.2ch; text-align: left; }

.lg-steps {
  list-style: none;
  margin: 0 0 18px;
  padding: 14px 16px;
  background: var(--fm-paper);
  border-radius: 14px;
  text-align: left;
  display: flex;
  flex-direction: column;
  gap: 11px;
}
.lg-step { display: flex; align-items: center; gap: 11px; font-size: 14px; font-weight: 600; color: var(--fm-faint); transition: color 0.25s ease; }
.lg-step-dot {
  width: 20px; height: 20px;
  border-radius: 50%;
  border: 2px solid #D9D3CA;
  flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  transition: all 0.25s var(--fm-ease);
}
.lg-step.is-active { color: var(--fm-ink); }
.lg-step.is-active .lg-step-dot { border-color: var(--fm-orange); border-top-color: transparent; animation: lgSpin 0.8s linear infinite; }
@keyframes lgSpin { to { transform: rotate(360deg); } }
.lg-step.is-done { color: var(--fm-ink-2); }
.lg-step.is-done .lg-step-dot { border-color: var(--fm-teal); background: var(--fm-teal); }
.lg-step-dot svg { width: 13px; height: 13px; fill: none; stroke: #fff; stroke-width: 3; stroke-linecap: round; stroke-linejoin: round; }

.lg-loader-tip { margin: 0; font-size: 13px; color: var(--fm-muted); font-weight: 600; min-height: 1.4em; }
.lg-tip-enter-active, .lg-tip-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.lg-tip-enter { opacity: 0; transform: translateY(6px); }
.lg-tip-leave-to { opacity: 0; transform: translateY(-6px); }
.lg-overlay-enter-active, .lg-overlay-leave-active { transition: opacity 0.3s ease; }
.lg-overlay-enter, .lg-overlay-leave-to { opacity: 0; }

/* ===================================================== */
/* SHELL + CARD                                          */
/* ===================================================== */
.lg-shell {
  width: 100%;
  max-width: 1080px;
  opacity: 0;
  transform: translateY(14px);
  transition: opacity 0.5s var(--fm-ease), transform 0.5s var(--fm-ease), filter 0.3s ease;
}
.lg-shell.is-loaded { opacity: 1; transform: none; }
.lg-shell.is-blurred { filter: blur(6px); pointer-events: none; }

.lg-card {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr);
  min-height: 580px;
  background: #fff;
  border: 1px solid var(--fm-line);
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 30px 70px -30px rgba(34, 31, 28, 0.5);
}

/* ===================================================== */
/* FORM PANE                                             */
/* ===================================================== */
.lg-form-pane {
  padding: 44px 48px;
  display: flex;
  flex-direction: column;
  transition: opacity 0.3s ease, visibility 0.3s ease;
}
.lg-form-pane.is-hidden { opacity: 0; visibility: hidden; pointer-events: none; }

.lg-brand-mark { display: flex; align-items: center; gap: 12px; }
.lg-brand-logo {
  width: 42px; height: 42px; border-radius: 12px;
  background: var(--fm-orange); color: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  font-family: 'Sora', sans-serif; font-weight: 800; font-size: 16px;
  flex-shrink: 0;
}
.lg-brand-name { font-family: 'Sora', sans-serif; font-size: 15px; font-weight: 800; }
.lg-brand-sub { font-size: 13px; color: var(--fm-muted); font-weight: 600; margin-top: 1px; }

.lg-form-block {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 400px;
  width: 100%;
  padding-top: 28px;
}
.lg-form-title { margin-bottom: 26px; }
.lg-form-title h1 {
  font-family: 'Sora', sans-serif;
  font-size: 32px; font-weight: 800;
  letter-spacing: -0.025em; line-height: 1.1;
  margin: 0 0 8px; color: var(--fm-ink);
}
.lg-form-title p { margin: 0; color: var(--fm-muted); font-size: 15px; line-height: 1.55; }
.lg-form-title strong { color: var(--fm-ink); font-weight: 700; }

.lg-form { display: flex; flex-direction: column; gap: 16px; margin: 0; }
.lg-form.is-shaking { animation: lgShake 0.42s ease; }
@keyframes lgShake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-7px); }
  40% { transform: translateX(6px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(2px); }
}

/* ---------- Fields: one flex row, no absolute positioning ---------- */
.lg-field { display: flex; flex-direction: column; gap: 7px; }
.lg-field-label { font-size: 13.5px; font-weight: 700; color: var(--fm-ink-2); display: block; }

.lg-control {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 48px;
  padding: 0 6px 0 14px;
  background: var(--fm-paper);
  border: 1.5px solid var(--fm-line);
  border-radius: 12px;
  transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
}
.lg-control:hover { border-color: #D3CCC1; }
.lg-control:focus-within {
  border-color: var(--fm-orange);
  background: #fff;
  box-shadow: 0 0 0 4px rgba(238, 122, 34, 0.16);
}
.lg-control.is-error { border-color: #F0B4AE; background: #FFFBFA; }
.lg-control.is-error:focus-within { border-color: var(--fm-orange); background: #fff; }
.lg-control.is-disabled { background: #F1EEE9; opacity: 0.8; }

.lg-control-icon { font-size: 16px; color: var(--fm-faint); transition: color 0.15s ease; }
.lg-control:focus-within .lg-control-icon { color: var(--fm-orange-dark); }

.lg-control-input {
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0 8px 0 0;
  margin: 0;
  border: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  color: var(--fm-ink);
  -webkit-appearance: none;
  appearance: none;
}
.lg-control-input::placeholder { color: var(--fm-faint); font-weight: 500; opacity: 1; }
.lg-control-input:disabled { color: var(--fm-faint); cursor: not-allowed; }
.lg-control-input::-ms-reveal { display: none; }

.lg-control--select { padding-right: 14px; position: relative; }
.lg-control--select select.lg-control-input { cursor: pointer; padding-right: 4px; }
.lg-select-caret { font-size: 14px; color: var(--fm-muted); pointer-events: none; }

.lg-eye-btn {
  width: 36px; height: 36px;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--fm-muted);
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 17px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.lg-eye-btn:hover:not(:disabled) { background: var(--fm-orange-soft); color: var(--fm-orange-dark); }
.lg-eye-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.lg-hint { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; font-weight: 700; color: #9A5B0C; }

/* ---------- Options row ---------- */
.lg-form-options { display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; }

.lg-check {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  font-size: 14px;
  font-weight: 600;
  color: var(--fm-ink-2);
  cursor: pointer;
  user-select: none;
}
.lg-check input { position: absolute; opacity: 0; width: 1px; height: 1px; pointer-events: none; }
.lg-check-box {
  width: 20px; height: 20px;
  border-radius: 6px;
  border: 2px solid #D3CCC1;
  background: #fff;
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s ease;
}
.lg-check-box svg {
  width: 13px; height: 13px;
  fill: none; stroke: #fff; stroke-width: 3.2;
  stroke-linecap: round; stroke-linejoin: round;
  opacity: 0; transform: scale(0.6);
  transition: all 0.15s var(--fm-ease);
}
.lg-check input:checked + .lg-check-box { background: var(--fm-orange); border-color: var(--fm-orange); }
.lg-check input:checked + .lg-check-box svg { opacity: 1; transform: scale(1); }
.lg-check input:focus-visible + .lg-check-box { box-shadow: 0 0 0 3px rgba(238, 122, 34, 0.35); }
.lg-check.is-disabled { opacity: 0.6; cursor: not-allowed; }

.lg-link { color: var(--fm-orange-dark); font-weight: 700; font-size: 14px; text-decoration: none; border-radius: 4px; }
.lg-link:hover { color: #B5520B; text-decoration: underline; text-underline-offset: 3px; }

/* ---------- Alert ---------- */
.lg-alert {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: var(--fm-danger-soft);
  border: 1px solid #FBD5D0;
  border-radius: 12px;
  color: var(--fm-danger);
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.45;
}
.lg-alert-icon { font-size: 17px; margin-top: 1px; }
.lg-alert.is-dark { background: rgba(240, 68, 56, 0.12); border-color: rgba(240, 68, 56, 0.35); color: #FDA29B; }
.lg-attempts { display: flex; align-items: center; gap: 8px; margin-top: 6px; font-size: 12.5px; color: #912018; }
.lg-attempt-pips { display: inline-flex; gap: 4px; }
.lg-attempt-pips i { display: block; width: 16px; height: 4px; border-radius: 2px; background: #F6C9C4; }
.lg-attempt-pips i.is-used { background: var(--fm-danger); }

/* ---------- Location list ---------- */
.lg-loc-list { display: flex; flex-direction: column; gap: 8px; }
.lg-loc {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  background: #fff;
  border: 1.5px solid var(--fm-line);
  border-radius: 12px;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}
.lg-loc:hover:not(:disabled) { border-color: #D3CCC1; background: var(--fm-paper); }
.lg-loc.is-selected { border-color: var(--fm-orange); background: var(--fm-orange-soft); box-shadow: 0 0 0 3px rgba(238, 122, 34, 0.12); }
.lg-loc-icon {
  width: 36px; height: 36px; border-radius: 10px;
  background: var(--fm-paper); color: var(--fm-muted);
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 16px; flex-shrink: 0;
  transition: all 0.15s ease;
}
.lg-loc.is-selected .lg-loc-icon { background: var(--fm-orange); color: #fff; }
.lg-loc-name { flex: 1; font-size: 15px; font-weight: 700; color: var(--fm-ink); }
.lg-loc-radio { width: 20px; height: 20px; border-radius: 50%; border: 2px solid #D3CCC1; flex-shrink: 0; transition: all 0.15s ease; }
.lg-loc.is-selected .lg-loc-radio { border-color: var(--fm-orange); background: var(--fm-orange); box-shadow: inset 0 0 0 4px #fff; }

/* ---------- Buttons (native, fully owned here) ---------- */
.lg-btn-primary {
  width: 100%;
  height: 50px;
  padding: 0 18px;
  margin-top: 4px;
  background: var(--fm-orange);
  border: 0;
  border-radius: 12px;
  color: #fff;
  font-weight: 800;
  font-size: 15.5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  box-shadow: 0 10px 22px -12px rgba(238, 122, 34, 0.9);
  transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
}
.lg-btn-primary:hover:not(:disabled) { background: var(--fm-orange-dark); transform: translateY(-1px); box-shadow: 0 14px 26px -12px rgba(212, 98, 15, 0.9); }
.lg-btn-primary:active:not(:disabled) { transform: translateY(0); }
.lg-btn-primary:disabled { opacity: 0.55; box-shadow: none; cursor: not-allowed; }
.lg-btn-primary.is-loading { background: var(--fm-orange-dark); opacity: 1; }
.lg-btn-arrow { font-size: 22px; transition: transform 0.15s ease; }
.lg-btn-primary:hover:not(:disabled) .lg-btn-arrow { transform: translateX(3px); }

.lg-btn-loader { display: inline-flex; gap: 5px; align-items: center; }
.lg-dot { width: 7px; height: 7px; background: #fff; border-radius: 50%; animation: lgDotBounce 1.2s ease-in-out infinite; }
.lg-dot:nth-child(2) { animation-delay: 0.15s; }
.lg-dot:nth-child(3) { animation-delay: 0.3s; }
@keyframes lgDotBounce { 0%, 60%, 100% { transform: translateY(0); opacity: 0.7; } 30% { transform: translateY(-5px); opacity: 1; } }

.lg-btn-text {
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 0;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
  color: var(--fm-muted);
  cursor: pointer;
  transition: color 0.15s ease, background 0.15s ease;
}
.lg-btn-text:hover:not(:disabled) { color: var(--fm-ink); background: var(--fm-paper); }
.lg-btn-text.is-light { color: rgba(255, 255, 255, 0.7); }
.lg-btn-text.is-light:hover { color: #fff; background: rgba(255, 255, 255, 0.08); }

.lg-btn-primary:focus-visible, .lg-btn-text:focus-visible, .lg-eye-btn:focus-visible,
.lg-link:focus-visible, .lg-loc:focus-visible, .lg-back-btn:focus-visible {
  outline: 3px solid rgba(238, 122, 34, 0.55);
  outline-offset: 2px;
}
.lg-page button:focus:not(:focus-visible) { outline: none; }

/* ===================================================== */
/* SIDE PANE                                             */
/* ===================================================== */
.lg-side-pane { position: relative; display: flex; background: var(--fm-ink); color: #fff; overflow: hidden; }
.lg-side-pane::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(520px 320px at 100% 0%, rgba(238, 122, 34, 0.22), transparent 60%),
    radial-gradient(520px 420px at 0% 100%, rgba(15, 110, 107, 0.26), transparent 65%);
  pointer-events: none;
}
.lg-side-content { position: relative; z-index: 1; width: 100%; padding: 44px 46px; display: flex; flex-direction: column; gap: 24px; }

.lg-route { display: block; width: 100%; max-width: 340px; height: auto; overflow: visible; }
.lg-route-base { fill: none; stroke: rgba(255, 255, 255, 0.14); stroke-width: 2; stroke-dasharray: 2 7; stroke-linecap: round; }
.lg-route-live { fill: none; stroke: var(--fm-amber); stroke-width: 2.5; stroke-linecap: round; stroke-dasharray: 46 400; animation: lgRoute 4.5s var(--fm-ease) infinite; }
@keyframes lgRoute { from { stroke-dashoffset: 46; } to { stroke-dashoffset: -340; } }
.lg-route-node circle { fill: var(--fm-ink); stroke: rgba(255, 255, 255, 0.45); stroke-width: 2; }
.lg-route-node.is-end circle { fill: var(--fm-orange); stroke: rgba(238, 122, 34, 0.35); stroke-width: 6; }
.lg-route-node text { fill: rgba(255, 255, 255, 0.55); font-family: 'Manrope', sans-serif; font-size: 11px; font-weight: 700; text-anchor: middle; }

.lg-side-title { font-family: 'Sora', sans-serif; font-size: 40px; font-weight: 800; line-height: 1.05; letter-spacing: -0.03em; color: #fff; margin: 0; }
.lg-side-title.is-sm { font-size: 30px; }
.lg-side-sub { margin-top: 8px; font-size: 15px; font-weight: 700; color: var(--fm-amber); }
.lg-side-tagline { margin: 14px 0 0; max-width: 40ch; color: rgba(255, 255, 255, 0.7); font-size: 15px; line-height: 1.6; }
.lg-side-tagline strong { color: #fff; }

.lg-side-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 16px; }
.lg-side-list li { display: flex; align-items: flex-start; gap: 13px; }
.lg-side-icon {
  width: 40px; height: 40px; border-radius: 12px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: var(--fm-amber);
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 17px; flex-shrink: 0;
}
.lg-side-list strong { display: block; font-family: 'Sora', sans-serif; font-size: 14.5px; font-weight: 700; }
.lg-side-list li > div > span { display: block; margin-top: 2px; font-size: 13.5px; line-height: 1.5; color: rgba(255, 255, 255, 0.62); }

.lg-side-foot { margin-top: auto; padding-top: 16px; border-top: 1px solid rgba(255, 255, 255, 0.1); font-size: 12.5px; font-weight: 600; color: rgba(255, 255, 255, 0.48); }

/* Reset */
.lg-back-btn {
  align-self: flex-start;
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: #fff;
  font-size: 13px; font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;
}
.lg-back-btn:hover { background: rgba(255, 255, 255, 0.15); transform: translateX(-2px); }

.lg-reset-block { display: flex; flex-direction: column; gap: 24px; }
.lg-reset-form { display: flex; flex-direction: column; gap: 16px; margin: 0; }
.lg-reset-form .lg-field-label { color: rgba(255, 255, 255, 0.75); }

.lg-control.is-dark { background: rgba(255, 255, 255, 0.06); border-color: rgba(255, 255, 255, 0.14); }
.lg-control.is-dark:hover { border-color: rgba(255, 255, 255, 0.24); }
.lg-control.is-dark:focus-within { border-color: var(--fm-amber); background: rgba(255, 255, 255, 0.1); box-shadow: 0 0 0 4px rgba(238, 122, 34, 0.22); }
.lg-control.is-dark .lg-control-icon { color: rgba(255, 255, 255, 0.4); }
.lg-control.is-dark:focus-within .lg-control-icon { color: var(--fm-amber); }
.lg-control.is-dark .lg-control-input { color: #fff; }
.lg-control.is-dark .lg-control-input::placeholder { color: rgba(255, 255, 255, 0.38); }

.lg-sent { align-items: flex-start; gap: 16px; }
.lg-sent-icon {
  width: 64px; height: 64px; border-radius: 18px;
  background: rgba(15, 110, 107, 0.3);
  border: 1px solid rgba(94, 200, 193, 0.35);
  display: flex; align-items: center; justify-content: center;
}
.lg-sent-icon svg { width: 34px; height: 34px; fill: none; stroke: #8EE0D9; stroke-width: 2.4; stroke-linecap: round; stroke-linejoin: round; }
.lg-sent-icon path { stroke-dasharray: 44; stroke-dashoffset: 44; animation: lgDraw 0.5s 0.15s var(--fm-ease) forwards; }
.lg-sent-actions { display: flex; flex-direction: column; gap: 8px; width: 100%; margin-top: 4px; }

/* ===================================================== */
/* TRANSITIONS                                           */
/* ===================================================== */
.lg-fade-enter-active, .lg-fade-leave-active { transition: opacity 0.2s ease; }
.lg-fade-enter, .lg-fade-leave-to { opacity: 0; }
.lg-fade-up-enter-active { transition: opacity 0.3s ease, transform 0.3s var(--fm-ease); }
.lg-fade-up-leave-active { transition: opacity 0.15s ease; }
.lg-fade-up-enter { opacity: 0; transform: translateY(-6px); }
.lg-fade-up-leave-to { opacity: 0; }
.lg-step-swap-enter-active, .lg-step-swap-leave-active { transition: opacity 0.25s ease, transform 0.3s var(--fm-ease); }
.lg-step-swap-enter { opacity: 0; transform: translateX(18px); }
.lg-step-swap-leave-to { opacity: 0; transform: translateX(-18px); }
.lg-pane-slide-enter-active, .lg-pane-slide-leave-active { transition: opacity 0.3s ease, transform 0.35s var(--fm-ease); }
.lg-pane-slide-enter { opacity: 0; transform: translateX(24px); }
.lg-pane-slide-leave-to { opacity: 0; transform: translateX(-24px); }

/* ===================================================== */
/* RESPONSIVE                                            */
/* ===================================================== */
@media (max-width: 900px) {
  .lg-card { grid-template-columns: 1fr; min-height: 0; }
  .lg-form-pane { padding: 32px; }
  .lg-form-pane.is-hidden { display: none; }
  .lg-form-block { max-width: none; }
  .lg-side-content { padding: 32px; }
  .lg-side-title { font-size: 30px; }
}
@media (max-width: 600px) {
  .lg-route, .lg-side-list { display: none; }
}
@media (max-width: 480px) {
  .lg-page { padding: 12px; }
  .lg-card { border-radius: 20px; }
  .lg-form-pane, .lg-side-content { padding: 24px 20px; }
  .lg-form-title h1 { font-size: 26px; }
  .lg-side-title, .lg-side-title.is-sm { font-size: 24px; }
  .lg-loader-card { padding: 26px 20px 20px; }
  .lg-control-input { font-size: 16px; } /* prevents iOS zoom */
}

/* ===================================================== */
/* REDUCED MOTION                                        */
/* ===================================================== */
@media (prefers-reduced-motion: reduce) {
  .lg-ring-orbit, .lg-ring-mark, .lg-route-live, .lg-dot,
  .lg-step.is-active .lg-step-dot, .lg-form.is-shaking, .lg-loader-card { animation: none !important; }
  .lg-shell, .lg-ring-progress,
  .lg-step-swap-enter-active, .lg-step-swap-leave-active,
  .lg-pane-slide-enter-active, .lg-pane-slide-leave-active { transition: none !important; }
  .lg-ring-check svg, .lg-sent-icon path { stroke-dashoffset: 0; animation: none !important; }
}
</style>


// WEBPACK FOOTER //
// src/components/importables/Login.vue